import * as Popover from '@radix-ui/react-popover';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Monitor } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { invalidateProjects } from '@/domains/projects/queries';
import { activityApi } from '@/shared/api/endpoints';
import type { ActivityItem } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';
import type { Locale } from '@/shared/i18n';
import { JobStatusBadge, ProgressBar, useToast } from '@/shared/ui';
import { routes } from '../routes';

export const activityKeys = {
  feed: (filters: Record<string, unknown>) => ['activity', 'feed', filters] as const,
  running: ['activity', 'running'] as const,
  tray: ['activity', 'tray'] as const,
};

/** Where the result of a given job will show up, so the tray can say so and link there. */
function resultLink(item: ActivityItem): { to: string; labelKey: string } | null {
  if (item.source === 'download') return { to: routes.libraryDownloads, labelKey: 'nav.libraryDownloads' };
  if (item.kind === 'export') return { to: routes.libraryExports, labelKey: 'nav.libraryExports' };
  if (item.project_id) return { to: routes.editor(item.project_id), labelKey: 'jobs.openProject' };
  return null;
}

/**
 * The global job tray. Polls a cheap count for the badge and the short feed while open, so a
 * long-running export is visible from any page — jobs do not live in one screen's React state.
 */
export function JobTray() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const running = useQuery({
    queryKey: activityKeys.running,
    queryFn: activityApi.running,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const activeCount = (running.data?.running ?? 0) + (running.data?.pending ?? 0);

  const feed = useQuery({
    queryKey: activityKeys.tray,
    queryFn: () => activityApi.list({ limit: 8 }),
    // Poll faster while something is in flight, then fall back to idle.
    refetchInterval: activeCount > 0 ? 2500 : false,
    staleTime: 2000,
  });

  // Announce transitions so a user on another page learns their export finished.
  const seenTerminal = useRef(new Map<string, string>());
  useEffect(() => {
    const items = feed.data?.items ?? [];
    for (const item of items) {
      const previous = seenTerminal.current.get(item.id);
      if (previous && previous !== item.status && (item.status === 'completed' || item.status === 'failed')) {
        toast({
          tone: item.status === 'completed' ? 'success' : 'error',
          title: t(item.status === 'completed' ? 'jobs.completedToast' : 'jobs.failedToast', {
            title: item.title,
          }),
          description:
            item.status === 'failed' && item.refunded
              ? t('jobs.refunded', { count: item.credits_charged })
              : undefined,
        });
        // A finished job changes credit, library contents and project state.
        void invalidateProjects(queryClient);
        void queryClient.invalidateQueries({ queryKey: ['library'] });
        void queryClient.invalidateQueries({ queryKey: ['activity'] });
      }
      seenTerminal.current.set(item.id, item.status);
    }
  }, [feed.data, toast, t, queryClient]);

  const items = feed.data?.items ?? [];
  const runningItems = items.filter((item) => item.status === 'running' || item.status === 'pending');
  const recentItems = items.filter((item) => item.status !== 'running' && item.status !== 'pending').slice(0, 5);

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={t('a11y.jobTray')}
        className={cn(
          'relative flex h-8 w-8 cursor-pointer items-center justify-center rounded',
          'text-fg-muted transition-colors duration-150 hover:bg-surface-hover hover:text-fg',
        )}
      >
        <ListChecks className="h-4 w-4" />
        {activeCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-2xs font-semibold tabular-nums text-accent-fg">
            {activeCount}
          </span>
        )}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-[min(22rem,calc(100vw-2rem))] animate-slide-up overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg"
        >
          <div className="border-b border-border px-4 py-2.5">
            <p className="text-sm font-semibold">{t('jobs.tray')}</p>
          </div>

          <div className="max-h-[22rem] overflow-y-auto">
            {items.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-fg-subtle">{t('jobs.none')}</p>
            )}

            {runningItems.length > 0 && (
              <Section title={t('jobs.running')}>
                {runningItems.map((item) => (
                  <TrayRow key={item.id} item={item} locale={i18n.language as Locale} />
                ))}
              </Section>
            )}

            {recentItems.length > 0 && (
              <Section title={t('jobs.recent')}>
                {recentItems.map((item) => (
                  <TrayRow key={item.id} item={item} locale={i18n.language as Locale} />
                ))}
              </Section>
            )}
          </div>

          <div className="border-t border-border px-4 py-2">
            <Popover.Close asChild>
              <Link to={routes.activity} className="cursor-pointer text-sm font-medium text-accent hover:underline">
                {t('common.viewAll')} →
              </Link>
            </Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="px-4 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wider text-fg-subtle">{title}</p>
      {children}
    </div>
  );
}

/** Shown only for work one of the user's own PCs took: the server is the unmarked case. */
export function RunsOnPc() {
  const { t } = useTranslation();
  return (
    <p className="mt-0.5 flex items-center gap-1 text-xs text-fg-subtle">
      <Monitor className="h-3 w-3 shrink-0" aria-hidden />
      {t('jobs.onYourPc')}
    </p>
  );
}

function TrayRow({ item, locale }: { item: ActivityItem; locale: Locale }) {
  const { t } = useTranslation();
  const link = resultLink(item);
  const active = item.status === 'running' || item.status === 'pending';

  return (
    <div className="px-4 py-2 transition-colors hover:bg-surface-hover">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{t(`jobs.type.${item.kind}`, { defaultValue: item.kind })}</p>
          <p className="truncate text-xs text-fg-muted">{item.title}</p>
          {item.runner === 'engine' && <RunsOnPc />}
        </div>
        <JobStatusBadge status={item.status} />
      </div>

      {active && (
        <ProgressBar
          className="mt-2"
          value={item.progress}
          indeterminate={!item.progress_is_determinate}
          label={
            item.progress_is_determinate
              ? t('a11y.progress', { percent: Math.round(item.progress * 100) })
              : t('a11y.progressIndeterminate')
          }
        />
      )}

      <div className="mt-1.5 flex items-center justify-between gap-2 text-2xs text-fg-subtle">
        <span>{formatRelative(item.updated_at ?? item.created_at, locale)}</span>
        {link && item.status === 'completed' && (
          <Popover.Close asChild>
            <Link to={link.to} className="cursor-pointer font-medium text-accent hover:underline">
              {t('jobs.viewResult')}
            </Link>
          </Popover.Close>
        )}
      </div>
    </div>
  );
}
